import { useState } from 'react';
import { Check, Copy, ExternalLink, ShieldCheck } from 'lucide-react';
import type { StaffProfileResponse } from '../../types/staff';
import { useDiscordPlayer } from '../../hooks/useDiscordPlayer';
import { PlayerProfile } from '../PlayerProfile';
import { StaffAvatar } from './StaffAvatar';
import { ConfidenceRing } from './ConfidenceRing';
import { StaffTeamChips } from './StaffTeamChips';
import { StaffPlayerBadges } from './StaffPlayerBadges';
import { StaffPlayerFacts } from './StaffPlayerFacts';
import { StaffHonourList } from './StaffHonourBadge';
import { formatSeconds } from '../../utils/staffProfile';
import type { StaffHonour } from '../../utils/staffTickets';

interface StaffProfileHeaderProps {
  data: StaffProfileResponse;
  /** Monthly ticket honours (MVP, raffle wins), newest first. */
  honours?: StaffHonour[];
}

/** Hero block: identity, linked player, Discord link, ticket honours, confidence gauge and snapshot facts. */
export const StaffProfileHeader = ({ data, honours = [] }: StaffProfileHeaderProps) => {
  const { uuid, username, stats, loading, notFound } = useDiscordPlayer(data.discord_id);
  const [copied, setCopied] = useState(false);
  const confidence = data.profile?.confidence ?? 'none';

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(data.discord_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — the id is visible as text anyway
    }
  };

  const facts: [string, string][] = [
    ['Profile built', data.built_at ? `${formatSeconds(data.generated_at - data.built_at)} ago` : 'never'],
    ['Newest signal', data.evidence?.newest_at ? `${formatSeconds(data.generated_at - data.evidence.newest_at)} ago` : '—'],
    ['Window', `${data.window_days} days`],
  ];

  return (
    <header className="glass-card relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none bg-[radial-gradient(110%_140%_at_0%_0%,rgb(var(--accent-t-500-rgb)/0.22),transparent_55%),radial-gradient(60%_80%_at_100%_100%,rgb(var(--accent-t-400-rgb)/0.10),transparent_60%)]"
        aria-hidden="true"
      />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-300/70 to-transparent" aria-hidden="true" />

      <div className="relative p-5 md:p-7 pb-0 md:pb-0 flex flex-wrap items-center gap-x-8 gap-y-6">
        <div className="flex items-start gap-4 md:gap-5 min-w-[min(100%,300px)] flex-1">
          <StaffAvatar discordId={data.discord_id} name={data.display_name} size="xl" glow className="hidden sm:inline-flex" />
          <StaffAvatar discordId={data.discord_id} name={data.display_name} size="lg" glow className="sm:hidden" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              {data.is_staff && (
                <span className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-[11px] font-semibold uppercase tracking-[0.12em] bg-accent-500/20 text-accent-300 border border-accent-500/30">
                  <ShieldCheck className="w-3 h-3" aria-hidden="true" />
                  Staff
                </span>
              )}
              <code className="text-xs font-mono text-glass-300">@{data.username}</code>
              <StaffHonourList honours={honours} max={3} />
            </div>
            <h1 className="text-3xl md:text-4xl 2xl:text-5xl font-bold text-white leading-[1.05] tracking-tight">{data.display_name}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              {uuid ? (
                <span className="inline-flex flex-wrap items-center gap-2">
                  <PlayerProfile uuid={uuid} name={username ?? undefined} showUuid={false} inline />
                  <StaffPlayerBadges stats={stats} />
                  <StaffTeamChips uuid={uuid} />
                </span>
              ) : loading ? (
                <span className="text-xs text-glass-400 animate-pulse">Resolving player…</span>
              ) : notFound ? (
                <span className="text-xs text-glass-500" title="No Minecraft account is linked to this Discord ID">
                  No linked player
                </span>
              ) : null}
              <a
                href={`https://discord.com/users/${data.discord_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-accent-300 hover:text-accent-200 hover:underline"
              >
                Discord profile
                <ExternalLink className="w-3 h-3" aria-hidden="true" />
              </a>
              <button
                type="button"
                onClick={copyId}
                className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md border border-glass-200/15 bg-glass-100/5 text-[11px] font-mono text-glass-400 hover:text-white hover:border-glass-300/40 transition-colors touch-manipulation"
                title="Copy Discord ID"
              >
                {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                {data.discord_id}
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-5 flex-shrink-0">
          <ConfidenceRing confidence={confidence} />
          <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1.5 text-xs">
            {facts.map(([k, v]) => (
              <div key={k} className="contents">
                <dt className="text-glass-500 whitespace-nowrap">{k}</dt>
                <dd className="text-white font-semibold tabular-nums whitespace-nowrap">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
      <div className="relative px-5 md:px-7 py-5 md:py-6">
        {uuid && stats ? (
          <StaffPlayerFacts uuid={uuid} stats={stats} />
        ) : (
          <p className="text-xs text-glass-500">
            {loading ? 'Looking up the linked Minecraft account…' : 'No Minecraft account is linked to this Discord ID, so player facts are unavailable.'}
          </p>
        )}
      </div>
    </header>
  );
};
